/**
 * The readable outline beside the Universe stage.
 *
 * Subjects are the first axis. Named entities are the second axis only for
 * multi-instance subjects (competitors). Facets
 * remain the third axis, so a competitor's campaigns never sit beside another
 * competitor's positioning as if they were equivalent rows.
 */
import type { UniverseGraph } from "@/types/brandUniverse.type";
import type { StageFact } from "./universeLayout";
import type { ResolvedSubject } from "./universeTaxonomy";

export type UniverseOutlineGroupKind =
  | "subject"
  | "entity"
  | "facet"
  | "general";

export type UniverseOutlineGroup = {
  key: string;
  label: string;
  facts: StageFact[];
  children?: UniverseOutlineGroup[];
  planned?: boolean;
  blurb?: string;
  kind: UniverseOutlineGroupKind;
  /** Set on subject rows: the taxonomy subject key, which is also the ring id. */
  subjectKey?: string;
};

const normaliseName = (value: string) => value.trim().toLocaleLowerCase();

function sortFacts(facts: StageFact[]): StageFact[] {
  return [...facts].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "new" ? -1 : 1;
    return a.learnedAt < b.learnedAt ? 1 : a.learnedAt > b.learnedAt ? -1 : 0;
  });
}

function facetGroups(
  subject: ResolvedSubject,
  facts: StageFact[],
  keyPrefix: string,
  catchAllLabel?: string,
): UniverseOutlineGroup[] {
  const knownFacetIds = new Set(subject.facets.map((facet) => facet.conceptId));
  const catchAll = sortFacts(
    facts.filter((fact) => !fact.conceptIds.some((id) => knownFacetIds.has(id))),
  );
  const groups: UniverseOutlineGroup[] = [];

  if (catchAllLabel) {
    groups.push({
      key: `${keyPrefix}:overview`,
      label: catchAllLabel ?? "Overview",
      blurb:
        catchAllLabel === "Other knowledge"
          ? "Competitor facts whose knowledge type has not been identified yet."
          : "Identity, relationship to the brand, and general context.",
      facts: catchAll,
      kind: "facet",
    });
  }

  subject.facets.forEach((facet) => {
    groups.push({
      key: `${keyPrefix}:facet:${facet.key}`,
      label: facet.label,
      blurb: facet.blurb,
      facts: sortFacts(facts.filter((fact) => fact.conceptIds.includes(facet.conceptId))),
      kind: "facet",
    });
  });

  subject.missingFacets.forEach((facet) => {
    groups.push({
      key: `${keyPrefix}:facet:${facet.key}`,
      label: facet.label,
      blurb: facet.blurb,
      facts: [],
      planned: true,
      kind: "facet",
    });
  });

  return groups;
}

type CompetitorBucket = {
  key: string;
  name: string;
  description: string;
  order: number;
  facts: StageFact[];
};

function competitorGroups(
  graph: UniverseGraph,
  subject: ResolvedSubject,
  facts: StageFact[],
): UniverseOutlineGroup[] {
  const buckets = new Map<string, CompetitorBucket>();
  const keyByName = new Map<string, string>();

  (graph.entities ?? [])
    .filter((entity) => entity.kind === "competitor")
    .forEach((entity) => {
      const key = `competitor:${entity.id}`;
      buckets.set(key, {
        key,
        name: entity.name,
        description: entity.description,
        order: entity.order,
        facts: [],
      });
      keyByName.set(normaliseName(entity.name), key);
    });

  const unassigned: StageFact[] = [];
  for (const fact of facts) {
    let key = fact.entityId ? `competitor:${fact.entityId}` : "";
    if (key && !buckets.has(key) && fact.entityName) {
      buckets.set(key, {
        key,
        name: fact.entityName,
        description: "",
        order: Number.MAX_SAFE_INTEGER,
        facts: [],
      });
      keyByName.set(normaliseName(fact.entityName), key);
    }
    if (!key && fact.entityName) key = keyByName.get(normaliseName(fact.entityName)) ?? "";
    if (!key && fact.entityName) {
      key = `competitor:snapshot:${normaliseName(fact.entityName)}`;
      buckets.set(key, {
        key,
        name: fact.entityName,
        description: "",
        order: Number.MAX_SAFE_INTEGER,
        facts: [],
      });
      keyByName.set(normaliseName(fact.entityName), key);
    }

    const bucket = key ? buckets.get(key) : undefined;
    if (bucket) bucket.facts.push(fact);
    else unassigned.push(fact);
  }

  const named = [...buckets.values()]
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
    .map<UniverseOutlineGroup>((bucket) => ({
      key: bucket.key,
      label: bucket.name,
      blurb: bucket.description || undefined,
      facts: [],
      children: facetGroups(subject, bucket.facts, bucket.key, "Overview"),
      kind: "entity",
    }));

  if (unassigned.length > 0) {
    named.unshift({
      key: "competitor:general",
      label: "Across the set",
      facts: [],
      children: facetGroups(
        subject,
        unassigned,
        "competitor:general",
        "Other knowledge",
      ),
      kind: "general",
    });
  }

  return named;
}

/** Build every row of the outline from the graph already in memory. */
export function buildUniverseOutline(
  graph: UniverseGraph,
  facts: StageFact[],
  subjects: ResolvedSubject[],
): UniverseOutlineGroup[] {
  const groups: UniverseOutlineGroup[] = [];

  for (const subject of subjects) {
    if (!subject.hasConcepts) {
      groups.push({
        key: `subject:${subject.key}`,
        label: subject.label,
        blurb: subject.blurb,
        facts: [],
        planned: true,
        kind: "subject",
        subjectKey: subject.key,
        children: subject.missingFacets.map((facet) => ({
          key: `facet:${facet.key}`,
          label: facet.label,
          blurb: facet.blurb,
          facts: [],
          planned: true,
          kind: "facet",
        })),
      });
      continue;
    }

    // Facts placed here, plus facts cross-filed here by a concept only this
    // subject owns (they list under that facet and echo on this ring).
    const inSubject = facts.filter(
      (fact) => fact.subjectKey === subject.key || fact.crossSubjectKeys.includes(subject.key),
    );
    // Group by named rival only once there is a rival to name. The API always
    // sends the array, so its presence says nothing; an empty one would drop the
    // facet rows that show what this subject is for.
    const hasNamedCompetitors =
      (graph.entities?.length ?? 0) > 0 ||
      inSubject.some((fact) => fact.entityId || fact.entityName);
    if (subject.key === "competitors" && hasNamedCompetitors) {
      groups.push({
        key: "subject:competitors",
        label: subject.label,
        blurb: subject.blurb,
        facts: [],
        children: competitorGroups(graph, subject, inSubject),
        kind: "subject",
        subjectKey: subject.key,
      });
      continue;
    }

    const children = facetGroups(subject, inSubject, `subject:${subject.key}`);
    groups.push({
      key: `subject:${subject.key}`,
      label: subject.label,
      blurb: subject.blurb,
      // Facts with no matching facet remain directly under ordinary subjects.
      facts: sortFacts(
        inSubject.filter(
          (fact) => !subject.facets.some((facet) => fact.conceptIds.includes(facet.conceptId)),
        ),
      ),
      children,
      kind: "subject",
      subjectKey: subject.key,
    });
  }

  const offRing = facts.filter((fact) => fact.ring < 0);
  if (offRing.length > 0) {
    groups.push({
      key: "concept:__unassigned",
      label: "UNSORTED",
      facts: sortFacts(offRing),
      kind: "subject",
    });
  }

  return groups;
}

/** Distinct fact count across this group and every descendant. */
export function countOutlineFacts(group: UniverseOutlineGroup): number {
  const ids = new Set(group.facts.map((fact) => fact.id));
  const visit = (children: UniverseOutlineGroup[] | undefined) =>
    children?.forEach((child) => {
      child.facts.forEach((fact) => ids.add(fact.id));
      visit(child.children);
    });
  visit(group.children);
  return ids.size;
}

/** Every disclosure row that must open to reveal a selected fact. */
export function findOutlineFactPath(
  groups: UniverseOutlineGroup[],
  factId: string,
  path: string[] = [],
): string[] | null {
  for (const group of groups) {
    const next = [...path, group.key];
    if (group.facts.some((fact) => fact.id === factId)) return next;
    const childPath = findOutlineFactPath(group.children ?? [], factId, next);
    if (childPath) return childPath;
  }
  return null;
}
